/**
 * VoiceController — /api/v1/voice
 * Mirrors: src/Aidepo.Api/Controllers/VoiceController.cs
 *
 * Two-step flow: POST /recognize → poll GET /result/{eventId} until status
 * is "completed" or "failed".
 */
import { apiGet, apiPostForm } from '../client.js';
import type { Uuid } from '../types.js';

export interface RecognizeResponse {
  readonly eventId: Uuid;
  readonly agentJobId: Uuid;
  readonly transcript: string;
  readonly whisperLatencyMs: number;
}

/** Структурное действие агента из @@RESULT (hh-4fc). */
export interface AgentActionWire {
  readonly kind: string;
  readonly name: string;
  readonly to: string | null;
  readonly from: string | null;
}

/** Вариант уточнения из @@RESULT (hh-5nh). */
export interface ClarifyOptionWire {
  readonly id: string;
  readonly label: string;
  readonly hint: string | null;
}

/** Agent job status carried back to the client. */
export const VoiceJobStatus = {
  Pending: 'pending',
  Processing: 'processing',
  Completed: 'completed',
  Failed: 'failed',
} as const;
export type VoiceJobStatus = (typeof VoiceJobStatus)[keyof typeof VoiceJobStatus];

export interface RecognizeResultResponse {
  readonly eventId: Uuid;
  readonly transcript: string;
  readonly status: string;
  readonly summary: string | null;
  readonly needsClarification: boolean;
  readonly whisperLatencyMs: number;
  readonly agentLatencyMs: number;
  readonly errorMessage: string | null;
  /** ChangeRecord для Undo из чата (hh-2dl). null — откат недоступен. */
  readonly undoChangeId?: Uuid | null;
  /** Структурные действия для баббла AISummary (hh-4fc). */
  readonly actions?: readonly AgentActionWire[];
  /** Варианты для баббла AIClarify (hh-5nh). */
  readonly clarifyOptions?: readonly ClarifyOptionWire[];
}

/** POST /api/v1/voice/recognize — multipart audio in, eventId out. */
export function recognizeVoice(audio: Blob, filename = 'command.webm'): Promise<RecognizeResponse> {
  const form = new FormData();
  form.append('audio', audio, filename);
  return apiPostForm<RecognizeResponse>('/api/v1/voice/recognize', form);
}

/** GET /api/v1/voice/result/{eventId} — poll for agent completion (every ~500ms). */
export function getVoiceResult(eventId: Uuid, signal?: AbortSignal): Promise<RecognizeResultResponse> {
  return apiGet<RecognizeResultResponse>(
    `/api/v1/voice/result/${eventId}`,
    signal !== undefined ? { signal } : undefined,
  );
}

/** A job status is terminal once it is no longer pending/processing. */
export function isTerminalStatus(status: string): boolean {
  return status !== VoiceJobStatus.Pending && status !== VoiceJobStatus.Processing;
}

export interface PollVoiceResultOptions {
  /** Стартовый интервал между опросами. По умолчанию 500мс. */
  readonly intervalMs?: number;
  readonly signal?: AbortSignal;
  /**
   * Бюджет ожидания по настенным часам, мс. Опрос идёт пока не вышел этот
   * срок. По умолчанию 130_000 — таймаут агента (RUNNER_AGENT_TIMEOUT=120с)
   * плюс запас на сетевой round-trip. Реальные прогоны имеют p95≈59с,
   * max≈83с, поэтому 40-секундный потолок прошлой версии давал ложные
   * таймауты на успешных длинных прогонах.
   */
  readonly deadlineMs?: number;
  /**
   * Потолок интервала при бэкоффе, мс. Интервал стартует с `intervalMs` и
   * растёт ×1.5 до этого значения — чтобы не молотить сервер 260 раз за
   * время длинного прогона. По умолчанию 2_000.
   */
  readonly maxIntervalMs?: number;
}

/**
 * Polls GET /voice/result/{eventId} until the agent job reaches a terminal
 * status (completed/failed). Polling continues until the wall-clock `deadlineMs`
 * budget (default 130s ≈ agent timeout 120s + buffer) is exceeded, with a gentle
 * interval backoff: starts at `intervalMs` (default 500ms) and grows ×1.5 toward
 * `maxIntervalMs` (default 2000ms) to keep request volume down while waiting.
 *
 * Throws if the supplied AbortSignal fires or the deadline is hit. The
 * deadline-exceeded message matches AGENT_PAT in errors.ts so humanizeError maps
 * a genuine timeout to MSG_AGENT_TIMEOUT.
 */
export async function pollVoiceResult(
  eventId: Uuid,
  opts?: PollVoiceResultOptions,
): Promise<RecognizeResultResponse> {
  const startInterval = opts?.intervalMs ?? 500;
  const deadlineMs = opts?.deadlineMs ?? 130_000;
  const maxIntervalMs = opts?.maxIntervalMs ?? 2_000;
  const signal = opts?.signal;

  const deadline = Date.now() + deadlineMs;
  let interval = startInterval;

  for (;;) {
    if (signal?.aborted === true) throw new DOMException('Polling aborted', 'AbortError');

    const result = await getVoiceResult(eventId, signal);
    if (isTerminalStatus(result.status)) return result;

    // Stop once the next sleep would push us past the budget — avoids an extra
    // request that lands after the deadline anyway.
    if (Date.now() + interval >= deadline) break;

    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        signal?.removeEventListener('abort', onAbort);
        resolve();
      }, interval);
      const onAbort = (): void => {
        clearTimeout(timer);
        reject(new DOMException('Polling aborted', 'AbortError'));
      };
      if (signal !== undefined) signal.addEventListener('abort', onAbort, { once: true });
    });

    interval = Math.min(Math.round(interval * 1.5), maxIntervalMs);
  }

  throw new Error(
    `pollVoiceResult: deadline (${deadlineMs}ms) exceeded for event ${eventId}`,
  );
}
